import { Box, Button, InputAdornment, Popper, TextField } from "@mui/material";
import { useEffect, useRef, type Ref } from "react";
import { ActionButton } from "../../../../components/ui";
import TransitionLoader from "../../../../components/ui/TransitionLoader";
import { useThemeContext } from "../../../../globalContext/global-context";
import { hostThemes } from "../../../../theme/hostThemes";
import type { ThemeV2 } from "../../../../util/types";
import type {
  HostControlBindings,
  PrimaryActionBinding,
  QuestionArrangement,
  QuestionInput,
  SecondaryActionBinding,
  TextInputBinding,
} from "../../../contracts";
import { usePendingMessage } from "../../shared/usePendingMessage";

export const formsInputSurface = (theme: ThemeV2) => ({
  bgcolor: theme.tokens.color.surface.card,
  border: `1px solid ${theme.tokens.color.action.primary}`,
  borderRadius: `${theme.tokens.shape.radius.lg}px`,
  boxShadow: theme.tokens.elevation["2"],
  padding: `${theme.tokens.spacing["1"]}px`,
  width: "100%",
});

export const FormsTextInput = ({
  input,
  arrangement,
  autoFocus = true,
  dense = false,
}: {
  input: TextInputBinding;
  arrangement: QuestionArrangement;
  autoFocus?: boolean;
  dense?: boolean;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus, input.fieldId]);

  return (
    <Box
      data-testid="forms-host-input-surface"
      sx={{
        ...formsInputSurface(theme),
        "& .MuiOutlinedInput-root": {
          minHeight: arrangement === "wide" ? 96 : undefined,
          borderRadius: `${theme.tokens.shape.radius.lg}px`,
          "& fieldset, &.Mui-focused fieldset, &:hover fieldset": {
            border: 0,
          },
        },
      }}
    >
      <TextField
        size={dense ? "small" : undefined}
        inputRef={inputRef}
        id={input.fieldId}
        aria-label={input.label}
        value={input.value}
        disabled={input.disabled}
        multiline={input.multiline}
        placeholder={input.hint ?? theme.copy.hints.multitext}
        onChange={(event) => input.onChange(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            (!input.multiline || !event.shiftKey) &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            input.onConfirm();
          }
        }}
        error={Boolean(input.value && input.error)}
        helperText={input.value ? input.error : undefined}
        slotProps={{
          input: {
            startAdornment: input.prefix ? (
              <InputAdornment position="start">{input.prefix}</InputAdornment>
            ) : undefined,
            endAdornment: input.suffix ? (
              <InputAdornment position="end">{input.suffix}</InputAdornment>
            ) : undefined,
          },
          htmlInput: {
            "data-testid": "text-input-field",
            "aria-label": input.label,
            "aria-describedby": `${input.fieldId}-feedback`,
            inputMode: input.inputMode,
          },
          formHelperText: { id: `${input.fieldId}-feedback` },
        }}
        fullWidth
      />
    </Box>
  );
};

export const FormsPrimaryAction = ({
  action,
  minWidth,
  testId,
  buttonRef,
}: {
  action: PrimaryActionBinding;
  minWidth?: number;
  testId: string;
  buttonRef?: Ref<HTMLButtonElement>;
}) => (
  <Button
    ref={buttonRef}
    data-testid={testId}
    type="button"
    variant="contained"
    disabled={action.disabled}
    aria-busy={action.pending}
    onClick={action.run}
    sx={{
      position: "static",
      alignSelf: "flex-end",
      minWidth,
      zIndex: 1,
    }}
  >
    {action.label}
  </Button>
);

const FormsSecondaryAction = ({
  action,
}: {
  action: SecondaryActionBinding;
}) => (
  <ActionButton
    testId="secondary-answer-action"
    currentVal={{ text: action.label, display_text: true }}
    isSelected={action.selected}
    ariaPressed={action.selected}
    disabled={action.disabled}
    onClick={action.run}
  />
);

export const FormsHostControls = ({
  controls,
  input,
  arrangement,
  autoFocus = true,
  dense = false,
}: {
  controls: HostControlBindings;
  input: QuestionInput;
  arrangement: QuestionArrangement;
  autoFocus?: boolean;
  dense?: boolean;
}) => {
  const fixedAction =
    arrangement === "wide" &&
    (Boolean(controls.textInput) || input === "custom");

  return (
    <>
      {controls.textInput && (
        <FormsTextInput
          input={controls.textInput}
          arrangement={arrangement}
          autoFocus={autoFocus}
          dense={dense}
        />
      )}
      {(controls.secondaryAction || controls.primaryAction) && (
        <Box
          data-testid="forms-action-group"
          sx={{
            display: "flex",
            alignItems: "stretch",
            justifyContent: "flex-end",
            gap: 1,
            "& > button": { margin: 0 },
            position: fixedAction ? "fixed" : "static",
            bottom: fixedAction
              ? hostThemes.forms.controls.wideSendBottom
              : undefined,
            right: fixedAction
              ? hostThemes.forms.controls.wideSendRight
              : undefined,
            zIndex: 1,
          }}
        >
          {controls.secondaryAction && (
            <FormsSecondaryAction action={controls.secondaryAction} />
          )}
          {controls.primaryAction && (
            <FormsPrimaryAction
              action={controls.primaryAction}
              testId="landscape-send-button"
            />
          )}
        </Box>
      )}
    </>
  );
};

export const FormsPendingStatus = () => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const message = usePendingMessage(theme.copy.status.transitions);

  return (
    <Box
      data-testid="forms-pending-status"
      sx={{
        color: theme.tokens.color.text.primary,
        padding: `${theme.tokens.spacing["2"]}px ${theme.tokens.spacing["3"]}px`,
        maxWidth: 320,
        pointerEvents: "none",
      }}
    >
      <TransitionLoader
        id="forms-pending-indicator"
        color="inherit"
        messageFirst
        message={message}
        announcement={
          theme.copy.status.transitions[0] ?? theme.copy.status.loading
        }
      />
    </Box>
  );
};

export const FormsPendingOverlay = ({
  open,
  anchorEl,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;

  if (!open) return null;
  if (!anchorEl) {
    return (
      <Box sx={{ position: "absolute", bottom: 0, right: 0, zIndex: 1 }}>
        <FormsPendingStatus />
      </Box>
    );
  }

  return (
    <Popper
      open
      disablePortal
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        {
          name: "offset",
          options: { offset: [0, theme.tokens.spacing["2"]] },
        },
      ]}
      sx={{ zIndex: 1 }}
    >
      <FormsPendingStatus />
    </Popper>
  );
};
